/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { type ChangeEvent, useCallback, useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Pagination } from '../../components/ui/pagination.js';
import { QueryState } from '../../components/ui/query-state.js';
import {
  type Session,
  type SessionsPageParams,
  type SessionStatus,
} from '../../lib/api/sessions.js';
import { useClients, useTherapists } from '../../lib/queries/clinical.js';
import { useSessions, useSessionsPage } from '../../lib/queries/sessions.js';
import { uiStore } from '../../lib/stores/ui-store.js';

import { MonthCalendar } from './month-calendar.js';
import { ScheduleCalendar } from './schedule-calendar.js';
import { SessionForm } from './session-form.js';
import { SessionsList } from './session-list.js';

type ViewMode = 'list' | 'week' | 'day' | 'month';
const VIEWS: readonly ViewMode[] = ['list', 'week', 'day', 'month'];

function ViewToggle({
  view,
  onView,
}: {
  readonly view: ViewMode;
  readonly onView: (v: ViewMode) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const base = 'focus-ring rounded-xl px-4 py-2 text-sm font-medium';
  const on = 'bg-[var(--accent)] text-white';
  const off = 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300';
  const select = useCallback(
    (v: ViewMode) => () => {
      onView(v);
    },
    [onView],
  );
  return (
    <div className="mb-4 flex gap-2">
      {VIEWS.map((v) => (
        <button
          key={v}
          type="button"
          onClick={select(v)}
          className={`${base} ${view === v ? on : off}`}
        >
          {t(`admin.sessions.view_${v}`)}
        </button>
      ))}
    </div>
  );
}

type StatusFilter = 'all' | SessionStatus;
const STATUSES: readonly StatusFilter[] = [
  'all',
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
  'no_show',
];
const PAGE_LIMIT = 20;

/** Build the server query params from the page's filter state. */
function pageParams(offset: number, q: string, status: StatusFilter): SessionsPageParams {
  return {
    limit: PAGE_LIMIT,
    offset,
    ...(q !== '' ? { q } : {}),
    ...(status !== 'all' ? { status } : {}),
  };
}

const Toolbar = observer(function Toolbar({
  status,
  onStatus,
}: {
  readonly status: StatusFilter;
  readonly onStatus: (value: StatusFilter) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    uiStore.setSearch(e.target.value);
  }, []);
  const onSelect = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      onStatus(e.target.value as StatusFilter);
    },
    [onStatus],
  );
  return (
    <div className="mb-4 flex flex-wrap gap-3">
      <input
        type="search"
        value={uiStore.search}
        onChange={onSearch}
        placeholder={t('admin.sessions.searchPlaceholder')}
        aria-label={t('admin.sessions.searchPlaceholder')}
        className="focus-ring h-11 flex-1 rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      />
      <select
        value={status}
        onChange={onSelect}
        aria-label={t('admin.sessions.statusFilter')}
        className="focus-ring h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      >
        {STATUSES.map((value) => (
          <option key={value} value={value}>
            {value === 'all' ? t('admin.sessions.allStatus') : t(`status.${value}`)}
          </option>
        ))}
      </select>
    </div>
  );
});

/** Calendar pane (week/day/month) with the therapist filter dropdown. */
function CalendarPane({
  view,
  calSessions,
  clientNames,
  therapists,
  therapistFilter,
  onTherapist,
}: {
  readonly view: 'week' | 'day' | 'month';
  readonly calSessions: readonly Session[];
  readonly clientNames: ReadonlyMap<string, string>;
  readonly therapists: readonly { readonly id: string; readonly fullName: string }[];
  readonly therapistFilter: string;
  readonly onTherapist: (e: ChangeEvent<HTMLSelectElement>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <div className="mb-4">
        <label htmlFor="sched-therapist" className="sr-only">
          {t('admin.sessions.filterTherapist')}
        </label>
        <select
          id="sched-therapist"
          value={therapistFilter}
          onChange={onTherapist}
          className="focus-ring h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        >
          <option value="">{t('admin.sessions.allTherapists')}</option>
          {therapists.map((th) => (
            <option key={th.id} value={th.id}>
              {th.fullName}
            </option>
          ))}
        </select>
      </div>
      {view === 'month' ? (
        <MonthCalendar sessions={calSessions} clientNames={clientNames} />
      ) : (
        <ScheduleCalendar sessions={calSessions} clientNames={clientNames} mode={view} />
      )}
    </>
  );
}

/** Admin → Schedule (Figma). Clinic-wide schedule: list + week/day/month calendar. */
export const AdminSessionsPage = observer(function AdminSessionsPage(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [view, setView] = useState<ViewMode>('list');
  const [therapistFilter, setTherapistFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [debouncedQ, setDebouncedQ] = useState('');
  const search = uiStore.search;
  const page = useSessionsPage(pageParams(offset, debouncedQ, status));
  const allSessions = useSessions();
  const clients = useClients();
  const therapists = useTherapists();

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQ(search.trim());
      setOffset(0);
    }, 300);
    return () => {
      clearTimeout(id);
    };
  }, [search]);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);
  const onStatus = useCallback((value: StatusFilter) => {
    setStatus(value);
    setOffset(0);
  }, []);
  const onTherapist = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setTherapistFilter(e.target.value);
  }, []);

  const clientNames = new Map((clients.data ?? []).map((c) => [c.id, c.fullName]));
  const therapistNames = new Map((therapists.data ?? []).map((th) => [th.id, th.fullName]));
  const rows = page.data?.items ?? [];
  const total = page.data?.total ?? 0;
  const calSessions = (allSessions.data ?? []).filter(
    (s) => therapistFilter === '' || s.therapistId === therapistFilter,
  );

  return (
    <div className="p-8">
      <PageHeader
        title={t('admin.sessions.title')}
        subtitle={t('admin.sessions.count', { count: total })}
        action={
          <Button onClick={openModal}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('admin.sessions.schedule')}
          </Button>
        }
      />
      <ViewToggle view={view} onView={setView} />

      {view === 'list' ? (
        <>
          <Toolbar status={status} onStatus={onStatus} />
          <QueryState
            isLoading={page.isLoading}
            isError={page.isError}
            isEmpty={rows.length === 0}
            loadingLabel={t('common.loading')}
            errorLabel={t('common.loadError')}
            emptyLabel={t('admin.sessions.empty')}
          >
            <SessionsList rows={rows} clientNames={clientNames} therapistNames={therapistNames} />
            <Pagination
              total={total}
              limit={PAGE_LIMIT}
              offset={offset}
              onOffsetChange={setOffset}
            />
          </QueryState>
        </>
      ) : (
        <CalendarPane
          view={view}
          calSessions={calSessions}
          clientNames={clientNames}
          therapists={therapists.data ?? []}
          therapistFilter={therapistFilter}
          onTherapist={onTherapist}
        />
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('admin.sessions.schedule')}
        description={t('admin.sessions.scheduleSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <SessionForm onDone={closeModal} />
      </Modal>
    </div>
  );
});
